import {
	EnvironmentOutlined,
	ExclamationCircleOutlined,
	InfoCircleOutlined,
	FileTextOutlined,
} from '@ant-design/icons';
import {
	Col,
	Divider,
	Modal,
	Radio,
	Row,
	Space,
	Tooltip,
	Typography,
	Checkbox,
} from 'antd';
import { postApi } from 'apis';
import { PermissionContext } from 'components/Global/AuthLayout';
import Buttons from 'components/Global/Buttons';
import ImageUpload from 'components/Global/imageUpload';
import InputField from 'components/Global/InputField';
import SelectDropdown from 'components/Global/SelectDropdown';
import { countryPhoneCodes } from 'constants/CurrencyData';
import { dateFormats, monthsForCompanySetup } from 'constants/Data';
import { countryStateData } from 'constants/StateCountryData';
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useReducer,
	useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store';
import {
	hasFormError,
	invalidText,
	toastText,
	validateFormData,
} from 'utils/utils';
import { fetchCompanyWizard } from '../../../../../../redux/actions/payrollSettingWizardAction';
import styles from './index.module.scss';
import './index.scss';
import { FaBuilding } from 'react-icons/fa';

const initialFormState = {
	companyBaseDetails: {
		companyName: '',
		email: null,
		financialYear: '',
		napsa: '',
		nihma: '',
		tpin: '',
		dateFormat: '',
		isThirdPartyLoan: false,
	},
	companyLocation: {
		address1: '',
		address2: '',
		city: '',
		state: '',
		country: '',
		postalCode: '',
	},
	companyContactDetail: {
		name: null,
		title: null,
		phone: null,
	},
	gratuitySettings: {
		calculationMethod: 'COMPANY_WISE',
		calculationMonth: null,
	},
};

// Initial state for form errors
const initialErrorState = {
	companyBaseDetails: {
		companyName: false,
		financialYear: false,
		napsa: false,
		nihma: false,
		tpin: false,
		dateFormat: false,
	},
	companyLocation: {
		address1: false,
		address2: false,
		city: false,
		state: false,
		country: false,
		postalCode: false,
	},
	companyContactDetail: {
		name: false,
		title: false,
		phone: false,
	},
	gratuitySettings: {
		calculationMethod: false,
		calculationMonth: false,
	},
};

// Form reducer to centralize state updates
const formReducer = (state: any, action: any) => {
	switch (action.type) {
		case 'UPDATE_FIELD':
			return {
				...state,
				[action.section]: {
					...state[action.section],
					[action.field]: action.value,
				},
			};
		case 'UPDATE_SECTION':
			return {
				...state,
				[action.section]: {
					...state[action.section],
					...action.data,
				},
			};
		case 'SET_ALL':
			return { ...action.data };
		default:
			return state;
	}
};

// Error reducer
const errorReducer = (state: any, action: any) => {
	switch (action.type) {
		case 'SET_ERROR':
			return {
				...state,
				[action.section]: {
					...state[action.section],
					[action.field]: action.hasError,
				},
			};
		case 'SET_SECTION_ERRORS':
			return {
				...state,
				[action.section]: {
					...state[action.section],
					...action.errors,
				},
			};
		case 'SET_ALL_ERRORS':
			return { ...action.errors };
		case 'CLEAR_ERRORS':
			return initialErrorState;
		default:
			return state;
	}
};

// Props type definition
type Props = {
	changeTab: (key: string) => void;
	onCancel: () => void;
	editData: any;
	isPayrollRun: any;
};

export const CompanyDetails = (props: Props) => {
	const { onCancel, changeTab, editData, isPayrollRun } = props;
	const context = useContext(PermissionContext);
	const dispatch = useDispatch<AppDispatch>();

	// State management with useReducer for better organization
	const [formData, dispatchFormData] = useReducer(
		formReducer,
		initialFormState
	);
	const [formErrors, dispatchFormErrors] = useReducer(
		errorReducer,
		initialErrorState
	);

	// UI state
	const [isLoading, setIsLoading] = useState(false);
	const [isPopupVisible, setIsPopupVisible] = useState(false);
	const [statutoryModal, setStatutoryModal] = useState(false);
	const [isSubmit, setIsSubmit] = useState(false);
	// Location data state
	const [countryOptions, setCountryOptions] = useState<any>([]);
	const [stateOptions, setStateOptions] = useState<any>([]);

	// Memoized extraction of state options based on country
	const extractStateOptions = useCallback((countryIso2: any) => {
		const country = countryStateData.data.find(
			(c) => c.iso2 === countryIso2
		);
		if (country) {
			return country.states.map((state) => ({
				label: state.name,
				value: state.state_code,
			}));
		}
		return [];
	}, []);

	// Generic field change handler - centralized for all form sections
	const handleFieldChange = useCallback(
		(
			value: any,
			field: any,
			section: any,
			required = false,
			regex = null
		) => {
			// Update form data
			dispatchFormData({
				type: 'UPDATE_FIELD',
				section,
				field,
				value,
			});

			// Validate field if required
			if (required) {
				let hasError = false;
				if (typeof value === 'string') {
					hasError = invalidText(value);
				} else if (Array.isArray(value)) {
					hasError = value.length === 0;
				}

				// Check against regex if provided
				if (!hasError && typeof value === 'string' && regex) {
					const _regex = new RegExp(regex);
					hasError = !_regex.test(value);
				}

				// Update error state
				dispatchFormErrors({
					type: 'SET_ERROR',
					section,
					field,
					hasError,
				});

				
			}
		},
		[]
	);

	// Special handler for country change to update states and phone code
	const handleCountryChange = useCallback(
		(value: any) => {
			// Update country value
			handleFieldChange(value, 'country', 'companyLocation', true);

			// Reset state when country changes
			handleFieldChange(null, 'state', 'companyLocation', true);

			// Update phone code based on country
			const phoneCode = countryPhoneCodes[value];
			if (phoneCode) {
				handleFieldChange(
					phoneCode,
					'phone',
					'companyContactDetail',
					false
				);
			}
		},
		[handleFieldChange]
	);

	// Form submission handler
	const handleSubmit = useCallback(async () => {
		// Validate all form fields
		const companyBaseDetailsErrors = validateFormData(
			formData.companyBaseDetails,
			formErrors.companyBaseDetails
		);

		const companyLocationErrors = validateFormData(
			formData.companyLocation,
			formErrors.companyLocation
		);

		const gratuitySettingsErrors = { ...formErrors.gratuitySettings };

		// Update error states
		dispatchFormErrors({
			type: 'SET_SECTION_ERRORS',
			section: 'companyBaseDetails',
			errors: companyBaseDetailsErrors,
		});

		dispatchFormErrors({
			type: 'SET_SECTION_ERRORS',
			section: 'companyLocation',
			errors: companyLocationErrors,
		});
		dispatchFormErrors({
			type: 'SET_SECTION_ERRORS',
			section: 'gratuitySettings',
			errors: gratuitySettingsErrors,
		});

		// Check if any errors exist
		const hasErrors =
			hasFormError(companyBaseDetailsErrors) ||
			hasFormError(companyLocationErrors) ||
			hasFormError(gratuitySettingsErrors);

		if (hasErrors) {
			return;
		}

		// If no errors, show confirmation or submit
		if (!statutoryModal) {
			setIsPopupVisible(true);
		} else {
			handleConfirmSubmit();
		}
	}, [formData, formErrors, statutoryModal]);

	// Handle submission after confirmation
	const handleConfirmSubmit = async () => {
		setIsPopupVisible(false);
		setIsLoading(true);

		try {
			// Prepare data for API
			const companyDetails = {
				financialYear: parseInt(
					formData.companyBaseDetails.financialYear,
					10
				),
				napsa: String(formData.companyBaseDetails.napsa),
				nihma: String(formData.companyBaseDetails.nihma),
				dateFormat: formData.companyBaseDetails.dateFormat,
				isThirdPartyLoan: formData.companyBaseDetails.isThirdPartyLoan,
				address1: formData.companyLocation.address1,
				address2: formData.companyLocation.address2,
				city: formData.companyLocation.city,
				state: formData.companyLocation.state,
				country: formData.companyLocation.country,
				postalCode: parseInt(formData.companyLocation.postalCode),
				calculationMethod: formData.gratuitySettings.calculationMethod,
				...(formData.gratuitySettings.calculationMethod ===
					'COMPANY_WISE' && {
					calculationMonth:
						formData.gratuitySettings.calculationMonth,
				}),
			};

			const hasContactInfo = Boolean(
				formData.companyContactDetail.name ||
					formData.companyContactDetail.title ||
					formData.companyContactDetail.phone
			);

			const contactDetails = {
				id: editData?.ContactDetails[0]?.id,
				name: formData.companyContactDetail.name || '',
				title: formData.companyContactDetail.title || '',
				phone: formData.companyContactDetail.phone || '',
			};

			// Prepare submission data
			const data = {
				companyDetails,
				...(hasContactInfo && { contactDetails }),
			};

			await postApi('/company/company-details', data);
			setIsSubmit(true);
			// Handle success
			toastText('Company details updated successfully', 'success');
			dispatch(fetchCompanyWizard());
			changeTab('bankDetails');
		} catch (error: any) {
			// Handle error
			const message =
				error?.response?.data?.message ||
				'Something went wrong in updating company details';
			toastText(message, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	// Load country options on component mount
	useEffect(() => {
		const options = countryStateData.data.map((country) => ({
			label: country.name,
			value: country.iso2,
		}));
		setCountryOptions(options);
	}, []);

	// Update state options when country changes
	useEffect(() => {
		if (formData.companyLocation.country) {
			const states = extractStateOptions(
				formData.companyLocation.country
			);
			setStateOptions(states);
		} else {
			setStateOptions([]);
		}
	}, [formData.companyLocation.country, extractStateOptions]);

	// Load existing data when available
	useEffect(() => {
		if (!editData) return;

		// Set company base details
		dispatchFormData({
			type: 'UPDATE_SECTION',
			section: 'companyBaseDetails',
			data: {
				companyName: editData.name,
				email: editData.email,
				tpin: editData.tpin,
				nihma: editData?.CompanyDetails?.nihma,
				napsa: editData?.CompanyDetails?.napsa,
				financialYear: editData?.CompanyDetails?.financialYear,
				dateFormat: editData?.CompanyDetails?.dateFormat,
				isThirdPartyLoan:
					editData?.CompanyDetails?.isThirdPartyLoan || false,
			},
		});

		// Set statutory modal flag
		setStatutoryModal(Boolean(editData?.CompanyDetails?.napsa));

		// Set company location
		dispatchFormData({
			type: 'UPDATE_SECTION',
			section: 'companyLocation',
			data: {
				address1: editData?.CompanyDetails?.address1,
				address2: editData?.CompanyDetails?.address2,
				city: editData?.CompanyDetails?.city,
				state: editData?.CompanyDetails?.state,
				country: editData?.CompanyDetails?.country,
				postalCode: editData?.CompanyDetails?.postalCode,
			},
		});

		// Set company contact detail
		dispatchFormData({
			type: 'UPDATE_SECTION',
			section: 'companyContactDetail',
			data: {
				name: editData?.ContactDetails[0]?.name,
				phone: editData?.ContactDetails[0]?.phone,
				title: editData?.ContactDetails[0]?.title,
			},
		});
		dispatchFormData({
			type: 'UPDATE_SECTION',
			section: 'gratuitySettings',
			data: {
				calculationMethod: editData?.CompanyDetails?.calculationMethod,
				calculationMonth: editData?.CompanyDetails?.calculationMonth,
			},
		});
	}, [editData]);

	// Button configuration
	const buttons = useMemo(
		() => [
			{
				text: 'Cancel',
				isLoading: false,
				className: 'btn-cancel',
				isSubmit: false,
				disabled: isLoading,
				onclick: () => {
					setStatutoryModal(false);
					onCancel();
				},
				fontSize: '1.8rem',
				minWidth: '12rem',
				minHeight: '4rem',
			},
			{
				text: 'Save & Next',
				isLoading: isLoading,
				className: 'btn-blue',
				isSubmit: false,
				disabled: false,
				onclick: () => {
					handleSubmit();
				},
				fontSize: '1.8rem',
				minWidth: '12rem',
				minHeight: '4rem',
			},
		],
		[isLoading, onCancel, handleSubmit]
	);
	// Helper function to get previous month
	const getPreviousMonth = useCallback(
		(financialYearMonth: string | number) => {
			if (!financialYearMonth) return null;

			const monthNum =
				typeof financialYearMonth === 'string'
					? parseInt(financialYearMonth, 10)
					: financialYearMonth;

			// If January (1), return December (12), otherwise subtract 1
			return monthNum === 1 ? 12 : monthNum - 1;
		},
		[]
	);

	// Set default gratuity month when financial year changes
	useEffect(() => {
		if (
			formData.companyBaseDetails.financialYear &&
			formData.gratuitySettings.calculationMethod === 'COMPANY_WISE'
		) {
			const defaultGratuityMonth = getPreviousMonth(
				formData.companyBaseDetails.financialYear
			);
			if (
				defaultGratuityMonth &&
				!formData.gratuitySettings.calculationMonth
			) {
				handleFieldChange(
					defaultGratuityMonth,
					'calculationMonth',
					'gratuitySettings',
					false
				);
			}
		}
	}, [
		formData.companyBaseDetails.financialYear,
		formData.gratuitySettings.calculationMethod,
		getPreviousMonth,
		handleFieldChange,
	]);

	return (
		<div>
			<p className={styles['form-container-head-warning']}>
				<b>
					<sup>*</sup>
				</b>{' '}
				Indicated mandatory fields
			</p>
			<div className={styles['scrollable-content']}>
				<Row className={styles['form-container-card']} gutter={20}>
					{/* Company Image */}
					<Col span={18} className={`${styles['col']}`}>
						<ImageUpload />
					</Col>

					{/* Company Base Details */}
					<Col span={12} className={`${styles['col']} margin-top-10`}>
						<InputField
							name="companyName"
							value={formData.companyBaseDetails.companyName}
							label="Company Name"
							required={true}
							helperText="Company name required"
							onChange={(value) =>
								handleFieldChange(
									value,
									'companyName',
									'companyBaseDetails',
									true
								)
							}
							isError={formErrors.companyBaseDetails.companyName}
							disabled={true}
						/>
					</Col>
					<Col span={6} className={`${styles['col']} margin-top-10`}>
						<InputField
							name="email"
							value={formData.companyBaseDetails.email}
							label="Email"
							required={false}
							helperText="Email required"
							onChange={(value) =>
								handleFieldChange(
									value,
									'email',
									'companyBaseDetails',
									false
								)
							}
							isError={formErrors.companyBaseDetails.email}
							disabled={true}
						/>
					</Col>
					<Col span={6} className={`${styles['col']} margin-top-10`}>
						<SelectDropdown
							placeholder="Financial Year Start"
							options={monthsForCompanySetup}
							value={formData.companyBaseDetails.financialYear}
							onChange={(value) =>
								handleFieldChange(
									value,
									'financialYear',
									'companyBaseDetails',
									true
								)
							}
							size="large"
							required={true}
							disabled={editData?.CompanyDetails?.financialYear}
							helperText="Financial Year is required"
							label="Financial Year Start"
							isError={
								formErrors.companyBaseDetails.financialYear
							}
						/>
					</Col>
					<Col span={6} className={`${styles['col']} margin-top-10`}>
						<Tooltip title="NAPSA stands for the National Pension Scheme Authority, which is a mandatory savings plan for employees.">
							<div>
								<InputField
									name="napsa"
									value={formData.companyBaseDetails.napsa}
									label="NAPSA"
									required={true}
									helperText="Napsa required"
									onChange={(value) =>
										handleFieldChange(
											value,
											'napsa',
											'companyBaseDetails',
											true
										)
									}
									isError={
										formErrors.companyBaseDetails.napsa
									}
									disabled={Boolean(
										editData?.CompanyDetails?.napsa ||
											isSubmit
									)}
								/>
							</div>
						</Tooltip>
					</Col>
					<Col span={6} className={`${styles['col']} margin-top-10`}>
						<Tooltip title="NHIMA stands for the National Health Insurance Management Authority, which is a mandatory savings plan for employees.">
							<div>
								<InputField
									name="nihma"
									value={formData.companyBaseDetails.nihma}
									label="NHIMA"
									required={true}
									helperText="Nhima required"
									onChange={(value) =>
										handleFieldChange(
											value,
											'nihma',
											'companyBaseDetails',
											true
										)
									}
									isError={
										formErrors.companyBaseDetails.nihma
									}
									disabled={Boolean(
										editData?.CompanyDetails?.nihma ||
											isSubmit
									)}
								/>
							</div>
						</Tooltip>
					</Col>
					<Col span={6} className={`${styles['col']} margin-top-10`}>
						<InputField
							name="tpin"
							value={formData.companyBaseDetails.tpin}
							label="TPIN"
							required={true}
							helperText="Tpin required"
							onChange={(value) =>
								handleFieldChange(
									value,
									'tpin',
									'companyBaseDetails',
									true
								)
							}
							disabled={true}
							isError={formErrors.companyBaseDetails.tpin}
						/>
					</Col>
					<Col span={6} className={`${styles['col']} margin-top-10`}>
						<SelectDropdown
							placeholder="Date Format"
							options={dateFormats}
							value={formData.companyBaseDetails.dateFormat}
							onChange={(value) =>
								handleFieldChange(
									value,
									'dateFormat',
									'companyBaseDetails',
									true
								)
							}
							size="large"
							required={true}
							helperText="Date Format is required"
							label="Date Format"
							isError={formErrors.companyBaseDetails.dateFormat}
						/>
					</Col>

					<Divider className={styles['dividerStyle']} />

					{/* Company Location */}
					<Col span={24} className={`${styles['col']}`}>
						<div
							style={{
								fontSize: '1.8rem',
								fontWeight: '500',
								color: '#584495',
							}}
						>
							Company Location
						</div>
					</Col>
					<Col span={12} className={`${styles['col']} margin-top-10`}>
						<InputField
							name="address1"
							value={formData.companyLocation.address1}
							label="Address 1"
							required={true}
							helperText="Address 1 required"
							onChange={(value) =>
								handleFieldChange(
									value,
									'address1',
									'companyLocation',
									true
								)
							}
							isError={formErrors.companyLocation.address1}
						/>
					</Col>
					<Col span={12} className={`${styles['col']} margin-top-10`}>
						<InputField
							name="address2"
							value={formData.companyLocation.address2}
							label="Address 2"
							required={true}
							helperText="Address 2 required"
							onChange={(value) =>
								handleFieldChange(
									value,
									'address2',
									'companyLocation',
									true
								)
							}
							isError={formErrors.companyLocation.address2}
						/>
					</Col>
					<Col span={6} className={`${styles['col']} margin-top-10`}>
						<SelectDropdown
							placeholder="Country"
							options={countryOptions}
							value={formData.companyLocation.country}
							onChange={handleCountryChange}
							size="large"
							required={true}
							helperText="Country is required"
							label="Country"
							isError={formErrors.companyLocation.country}
						/>
					</Col>
					<Col span={6} className={`${styles['col']} margin-top-10`}>
						<SelectDropdown
							placeholder="State"
							options={stateOptions}
							value={formData.companyLocation.state}
							onChange={(value) =>
								handleFieldChange(
									value,
									'state',
									'companyLocation',
									true
								)
							}
							size="large"
							required={true}
							helperText="State is required"
							label="State"
							isError={formErrors.companyLocation.state}
						/>
					</Col>
					<Col span={6} className={`${styles['col']} margin-top-10`}>
						<InputField
							name="city"
							value={formData.companyLocation.city}
							label="City"
							required={true}
							helperText="City required"
							onChange={(value) =>
								handleFieldChange(
									value,
									'city',
									'companyLocation',
									true
								)
							}
							isError={formErrors.companyLocation.city}
						/>
					</Col>
					<Col span={6} className={`${styles['col']} margin-top-10`}>
						<InputField
							name="postalCode"
							value={formData.companyLocation.postalCode}
							label="Postal Code"
							required={true}
							helperText="Postal Code required"
							onChange={(value) =>
								handleFieldChange(
									value,
									'postalCode',
									'companyLocation',
									true
								)
							}
							isError={formErrors.companyLocation.postalCode}
						/>
					</Col>

					<Divider className={styles['dividerStyle']} />

					{/* Contact Person Details */}
					<Col span={24} className={`${styles['col']}`}>
						<div
							style={{
								fontSize: '1.8rem',
								fontWeight: '500',
								color: '#584495',
							}}
						>
							Company's Contact Person Details
						</div>
					</Col>
					<Col span={12} className={`${styles['col']} margin-top-10`}>
						<InputField
							name="name"
							value={formData.companyContactDetail.name}
							label="Contact Name"
							required={false}
							onChange={(value) =>
								handleFieldChange(
									value,
									'name',
									'companyContactDetail',
									false
								)
							}
							isError={formErrors.companyContactDetail.name}
						/>
					</Col>
					<Col span={6} className={`${styles['col']} margin-top-10`}>
						<InputField
							name="title"
							value={formData.companyContactDetail.title}
							label="Title"
							required={false}
							onChange={(value) =>
								handleFieldChange(
									value,
									'title',
									'companyContactDetail',
									false
								)
							}
							isError={formErrors.companyContactDetail.title}
						/>
					</Col>
					<Col span={6} className={`${styles['col']} margin-top-10`}>
						<InputField
							name="phone"
							value={formData.companyContactDetail.phone}
							label="PhoneNumber"
							required={false}
							onChange={(value) =>
								handleFieldChange(
									value,
									'phone',
									'companyContactDetail',
									false
								)
							}
							isError={formErrors.companyContactDetail.phone}
						/>
					</Col>
					<Divider className={styles['dividerStyle']} />
					<Col span={24} className={`${styles['col']}`}>
						<div
							style={{
								fontSize: '1.8rem',
								fontWeight: '500',
								color: '#584495',
								marginBottom: '8px',
							}}
						>
							<FileTextOutlined style={{ marginRight: '8px' }} />
							Loan Deduction Configuration
						</div>
						<div
							style={{
								fontSize: '1.4rem',
								color: '#8c8c8c',
								marginBottom: '16px',
							}}
						>
							Configure third party loan import options for
							payroll processing
						</div>
					</Col>

					{/* Third Party Loan Import Checkbox */}
					<Col span={24} className={`${styles['col']}`}>
						<div
							style={{
								border: '1px solid #d9d9d9',
								borderRadius: '6px',
								padding: '16px',
								backgroundColor: 'white',
								marginBottom: '16px',
							}}
						>
							<Checkbox
								checked={
									formData.companyBaseDetails.isThirdPartyLoan
								}
								onChange={(e) =>
									handleFieldChange(
										e.target.checked,
										'isThirdPartyLoan',
										'companyBaseDetails',
										false
									)
								}
								style={{
									fontSize: '1.6rem',
									fontWeight: '500',
								}}
							>
								Enable Third Party Loan Import
							</Checkbox>
							<div
								style={{
									marginLeft: '24px',
									marginTop: '8px',
									color: '#595959',
									fontSize: '1.4rem',
								}}
							>
								When enabled, you can import loan deduction data
								from CSV files during payroll processing. This
								allows automatic calculation of loan EMIs and
								deductions from employee salaries.
							</div>
						</div>
						{formData.companyBaseDetails.isThirdPartyLoan && (
							<Col
								span={24}
								className={`${styles['col']} margin-top-10`}
								style={{
									border: '1px solid #e0e0e0',
									borderRadius: '8px',
									padding: '1rem',
									backgroundColor: '#f9f9f9',
								}}
							>
								<Typography.Text
									style={{
										color: '#722ed1',
										fontWeight: 600,
										fontSize: '1.4rem',
									}}
								>
									<InfoCircleOutlined
										style={{ marginRight: 8 }}
									/>
									Third Party Loan Import Enabled
								</Typography.Text>
								<div
									style={{
										marginTop: '8px',
										color: '#8c8c8c',
										fontSize: '1.2rem',
										marginLeft: '20px',
									}}
								>
									Remember to check the 'Third Party Loan
									Deduction' checkbox when adding deductions
									for loan upload.
								</div>
							</Col>
						)}
					</Col>

					{/* Gratuity and Leave Pay Configuration */}
					<Col span={24} className={`${styles['col']}`}>
						<div
							style={{
								fontSize: '1.8rem',
								fontWeight: '500',
								color: '#584495',
								marginBottom: '8px',
							}}
						>
							Configure Gratuity and Leave pay calculation methods
						</div>
						<div
							style={{
								fontSize: '1.4rem',
								color: '#8c8c8c',
								marginBottom: '16px',
							}}
						>
							Choose a calculation method that will apply to both
							Gratuity Pay and Leave Pay configurations
						</div>
					</Col>

					{/* Radio Group for Calculation Methods */}
					<Col span={24} className={`${styles['col']}`}>
						<Radio.Group
							value={formData.gratuitySettings.calculationMethod}
							disabled={isPayrollRun}
							onChange={(e) => {
								if (isPayrollRun) return;

								const value = e.target.value;
								handleFieldChange(
									value,
									'calculationMethod',
									'gratuitySettings',
									true
								);

								if (value !== 'COMPANY_WISE') {
									handleFieldChange(
										null,
										'calculationMonth',
										'gratuitySettings',
										false
									);
								}
							}}
							style={{ width: '100%' }}
						>
							<Space
								direction="vertical"
								style={{ width: '100%' }}
							>
								{/* Company-wise Option */}
								<div
									onClick={() => {
										if (isPayrollRun) return;
										handleFieldChange(
											'COMPANY_WISE',
											'calculationMethod',
											'gratuitySettings',
											true
										);
									}}
									style={{
										cursor: 'pointer',
										border: '1px solid',
										borderColor:
											formData.gratuitySettings
												.calculationMethod ===
											'COMPANY_WISE'
												? '#584495'
												: '#d9d9d9',
										borderRadius: '6px',
										padding: '16px',
										backgroundColor:
											formData.gratuitySettings
												.calculationMethod ===
											'COMPANY_WISE'
												? '#F3F0FA'
												: 'white',
									}}
								>
									<Radio
										value="COMPANY_WISE"
										style={{
											fontSize: '1.6rem',
											fontWeight: '500',
											pointerEvents: 'none', // prevents double firing
										}}
									>
										<FaBuilding /> Company-wise
									</Radio>
									<div
										style={{
											marginLeft: '24px',
											marginTop: '8px',
											color: '#595959',
											fontSize: '1.4rem',
										}}
									>
										Single calculation method applied
										uniformly across the entire company.
										Both Gratuity Pay and Leave Pay will be
										calculated during employee termination
										or at the end of the financial year.
									</div>
								</div>

								{/* Conditional Month Selection for Company-wise */}
								{formData.gratuitySettings.calculationMethod ===
									'COMPANY_WISE' && (
									<Col
										span={6}
										className={`${styles['col']} margin-top-10`}
									>
										<SelectDropdown
											placeholder="Select Month"
											options={monthsForCompanySetup}
											value={
												formData.gratuitySettings
													.calculationMonth
											}
											onChange={(value) =>
												handleFieldChange(
													value,
													'calculationMonth',
													'gratuitySettings',
													true
												)
											}
											size="middle"
											required={true}
											helperText="Gratuity and Leave Pay Calculation Month is required"
											label={
												<span
													style={{ fontSize: '14px' }}
												>
													Gratuity and Leave Pay
													Calculation Month
												</span>
											}
											isError={
												formErrors.gratuitySettings
													.calculationMonth
											}
											disabled={isPayrollRun}
										/>
									</Col>
								)}
								<div
									style={{
										marginLeft: '13px',
										color: '#8c8c8c',
										fontSize: '1.2rem',
									}}
								>
									Select the month when both gratuity and
									leave pay should be calculated for the
									entire company.
								</div>

								{/* Branch-wise Option */}
								<div
									onClick={() => {
										if (isPayrollRun) return;
										handleFieldChange(
											'BRANCH_WISE',
											'calculationMethod',
											'gratuitySettings',
											true
										);
									}}
									style={{
										cursor: 'pointer',
										border: '1px solid',
										marginTop: '10px',
										borderColor:
											formData.gratuitySettings
												.calculationMethod ===
											'BRANCH_WISE'
												? '#584495'
												: '#d9d9d9',
										borderRadius: '6px',
										padding: '16px',
										backgroundColor:
											formData.gratuitySettings
												.calculationMethod ===
											'BRANCH_WISE'
												? '#F3F0FA'
												: 'white',
									}}
								>
									<Radio
										value="BRANCH_WISE"
										style={{
											fontSize: '1.6rem',
											fontWeight: '500',
											pointerEvents: 'none', // ensures clicks only register on container
										}}
									>
										<EnvironmentOutlined /> Branch-wise
									</Radio>
									<div
										style={{
											marginLeft: '24px',
											marginTop: '8px',
											color: '#595959',
											fontSize: '1.4rem',
										}}
									>
										Different calculation schedules for each
										branch. Each branch can have its own
										specific month for both Gratuity Pay and
										Leave Pay calculation or during employee
										termination.
									</div>
								</div>
							</Space>

							{/* Additional Info for Branch-wise */}
							{formData.gratuitySettings.calculationMethod ===
								'BRANCH_WISE' && (
								<Col
									span={24}
									className={`${styles['col']} margin-top-10`}
									style={{
										border: '1px solid #e0e0e0',
										borderRadius: '8px',
										padding: '1rem',
										backgroundColor: '#f9f9f9',
									}}
								>
									<Typography.Text
										style={{
											color: '#722ed1',
											fontWeight: 600,
											fontSize: '1.4rem',
										}}
									>
										<InfoCircleOutlined
											style={{ marginRight: 8 }}
										/>
										Branch-wise Configuration Selected
									</Typography.Text>
									<div
										style={{
											marginTop: '8px',
											color: '#8c8c8c',
											fontSize: '1.2rem',
											marginLeft: '20px',
										}}
									>
										Both Gratuity Pay and Leave Pay will be
										calculated during employee termination
										or at specific months that can be set
										individually for each branch.
									</div>
								</Col>
							)}
						</Radio.Group>
					</Col>
				</Row>
			</div>

			{/* Buttons */}
			<div
				style={{
					display: 'flex',
					justifyContent: 'start',
					marginTop: '20px',
				}}
			>
				<Buttons buttons={buttons} />
			</div>

			{/* Confirmation Modal */}
			<Modal
				title={
					<>
						<ExclamationCircleOutlined
							style={{ color: 'red', marginRight: 8 }}
						/>
						Confirm Submission
					</>
				}
				open={isPopupVisible}
				closable={false}
				onOk={handleConfirmSubmit}
				onCancel={() => setIsPopupVisible(false)}
				okText="Confirm"
				cancelText="Cancel"
				maskClosable={false}
			>
				<p>
					Once saved, the <strong>Company Name</strong>,{' '}
					<strong>Email</strong>, <strong>NAPSA</strong>,{' '}
					<strong>NHIMA</strong>, and <strong>TPIN </strong>
					cannot be modified. Are you sure you want to proceed?
				</p>
			</Modal>
		</div>
	);
};
